// Implements: F-UIX-018, F-USG-008
/**
 * The client of "Distribución" (UIX.6): the organization's usage policy, its four writes, and
 * the organization's people to name the rows (S4d-1, S4d-2).
 *
 * Every address is built from the organization the caller passes, ids escaped. The server
 * decides access before it reads any id: a reader it refuses gets the `404` of an unknown
 * organization, a team lead's write `403`. A write is sent **at most once** (`retry: false`,
 * checklist 13): a lost answer is shown as a failure the person may repeat, never repeated for
 * them. Each write answers the whole view after it, which the caller puts in the cache.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type {
  PeopleListView,
  PersonLimitInput,
  TeamAllocationInput,
  UsagePolicyView,
} from "../../../types/usagePolicy";

function organizationBase(organizationId: string): string {
  return `${API_BASE}/organizations/${encodeURIComponent(organizationId)}`;
}

function policyBase(organizationId: string): string {
  return `${organizationBase(organizationId)}/usage-policy`;
}

/** A read or write whose body is missing is a broken answer, not an empty one. */
function required<T>(body: T | undefined, path: string): T {
  if (body === undefined) throw new Error(`empty answer from ${path}`);
  return body;
}

export async function fetchUsagePolicy(organizationId: string, signal?: AbortSignal): Promise<UsagePolicyView> {
  const path = policyBase(organizationId);
  return required(await httpRequest<UsagePolicyView>(path, { signal }), path);
}

export async function fetchOrganizationPeople(organizationId: string, signal?: AbortSignal): Promise<PeopleListView> {
  const path = `${organizationBase(organizationId)}/people`;
  return required(await httpRequest<PeopleListView>(path, { signal }), path);
}

export async function putPersonLimit(
  organizationId: string,
  personId: string,
  body: PersonLimitInput,
): Promise<UsagePolicyView> {
  const path = `${policyBase(organizationId)}/people/${encodeURIComponent(personId)}`;
  return required(await httpRequest<UsagePolicyView>(path, { method: "PUT", body, retry: false }), path);
}

export async function deletePersonLimit(organizationId: string, personId: string): Promise<UsagePolicyView> {
  const path = `${policyBase(organizationId)}/people/${encodeURIComponent(personId)}`;
  return required(await httpRequest<UsagePolicyView>(path, { method: "DELETE", retry: false }), path);
}

export async function putTeamAllocation(
  organizationId: string,
  teamId: string,
  body: TeamAllocationInput,
): Promise<UsagePolicyView> {
  const path = `${policyBase(organizationId)}/teams/${encodeURIComponent(teamId)}`;
  return required(await httpRequest<UsagePolicyView>(path, { method: "PUT", body, retry: false }), path);
}

export async function deleteTeamAllocation(organizationId: string, teamId: string): Promise<UsagePolicyView> {
  const path = `${policyBase(organizationId)}/teams/${encodeURIComponent(teamId)}`;
  return required(await httpRequest<UsagePolicyView>(path, { method: "DELETE", retry: false }), path);
}
